function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { isValidElement, useContext, useEffect } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { pxToRem } from "../../../common";
import { TourContext } from "../context";
function StyledTextButton(_ref) {
  var text = _ref.text,
    _onClick = _ref.onClick;
  return /*#__PURE__*/React.createElement(Button, {
    disableRipple: true,
    variant: "text",
    size: "small",
    onClick: function onClick(e) {
      return _onClick(e);
    },
    sx: {
      color: 'tour.step.actions.text.secondary',
      p: 0,
      minWidth: 'min-content'
    }
  }, text);
}
function StyledButton(_ref2) {
  var text = _ref2.text,
    _onClick2 = _ref2.onClick;
  return /*#__PURE__*/React.createElement(Button, {
    disableRipple: true,
    variant: "contained",
    size: "small",
    onClick: function onClick(e) {
      return _onClick2(e);
    },
    sx: {
      paddingY: '8px',
      paddingX: '12px',
      minWidth: 'min-content',
      marginLeft: 'auto',
      color: 'tour.step.actions.text.primary',
      backgroundColor: 'tour.step.actions.primary',
      '&:hover': {
        color: 'white'
      }
    }
  }, text);
}

/**
 *
 * @param { import('react-joyride').TooltipRenderProps } params
 * @param params.step
 * @param params.tooltipProps
 * @return {JSX.Element}
 * @constructor
 */
function TourTooltipContent(_ref3) {
  var isLastStep = _ref3.isLastStep,
    index = _ref3.index,
    step = _ref3.step,
    primaryProps = _ref3.primaryProps,
    backProps = _ref3.backProps,
    closeProps = _ref3.closeProps,
    tooltipProps = _ref3.tooltipProps;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var theme = useTheme();
  var _useContext = useContext(TourContext),
    currentActiveTourStep = _useContext.currentActiveTourStep,
    isErrorMessage = _useContext.isErrorMessage,
    incrementStep = _useContext.incrementStep,
    decrementStep = _useContext.decrementStep,
    exitTour = _useContext.exitTour,
    completeTour = _useContext.completeTour,
    resetFromError = _useContext.resetFromError,
    onTourTooltipClickNext = _useContext.onTourTooltipClickNext,
    onTourTooltipClickBack = _useContext.onTourTooltipClickBack,
    onTourTooltipClickExit = _useContext.onTourTooltipClickExit,
    onTourTooltipClickDone = _useContext.onTourTooltipClickDone;
  var onClickNext = primaryProps.onClick;
  var onClickBack = backProps.onClick;
  var onClickClose = closeProps.onClick;
  var title = step.title,
    content = step.content,
    hideBackButton = step.hideBackButton,
    hideCloseButton = step.hideCloseButton,
    disableScrolling = step.disableScrolling,
    _step$hideNextButton = step.hideNextButton,
    hideNextButton = _step$hideNextButton === void 0 ? false : _step$hideNextButton,
    _step$isModal = step.isModal,
    isModal = _step$isModal === void 0 ? false : _step$isModal;

  /**
   * Scrolls `TourTooltip` into view if `Step.disableScrolling` is `false`.
   */
  useEffect(function () {
    if (!disableScrolling) {
      var el = document.querySelector(step.target);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, []);
  return /*#__PURE__*/React.createElement(Box, _extends({}, tooltipProps, {
    sx: [{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minWidth: '200px',
      maxWidth: '250px',
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.palette.onboardingTour.background
    }, isModal && {
      position: 'absolute',
      top: '50vh',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 99,
      backgroundColor: theme.palette.background["default"],
      maxWidth: 'fit-content'
    }]
  }), /*#__PURE__*/React.createElement(Box, {
    sx: [{
      '& .MuiTypography-root': {
        fontSize: pxToRem(13),
        color: 'tour.step.text',
        textAlign: 'center',
        whiteSpace: 'break-spaces'
      }
    }, isModal && {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      gap: '12px',
      '& .MuiTypography-root': {
        color: 'tour.modal.text'
      }
    }]
  }, title && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    component: "h1",
    sx: {
      fontWeight: 700
    }
  }, t(title, '')), content && ( /*#__PURE__*/isValidElement(content) ? content : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, t(content, '')))), /*#__PURE__*/React.createElement(Box, {
    sx: [{
      display: 'flex',
      gap: pxToRem(16),
      width: '100%',
      justifyContent: 'space-between'
    }, isModal && {
      '& .MuiButton-text': {
        color: 'tour.modal.actions.text.secondary'
      }
    }]
  },
  // Close button
  (!hideCloseButton || isErrorMessage) && /*#__PURE__*/React.createElement(StyledTextButton, {
    text: t('tour_step_action_close'),
    onClick: function onClick(e) {
      exitTour();
      onClickClose(e);
      if (onTourTooltipClickExit) {
        onTourTooltipClickExit({
          tourStepId: currentActiveTourStep.id,
          stepId: isErrorMessage ? null : step.id,
          errorId: isErrorMessage ? step.id : null,
          isError: isErrorMessage,
          stepTitle: title,
          stepContent: content
        });
      }
    }
  }),
  // Back button
  !hideBackButton && (index > 0 || isErrorMessage) && /*#__PURE__*/React.createElement(StyledTextButton, {
    text: t('tour_step_action_back'),
    onClick: function onClick(e) {
      if (isErrorMessage) {
        resetFromError();
      } else {
        decrementStep();
        onClickBack(e);
      }
      if (onTourTooltipClickBack) {
        onTourTooltipClickBack({
          tourStepId: currentActiveTourStep.id,
          stepId: isErrorMessage ? null : step.id,
          errorId: isErrorMessage ? step.id : null,
          isError: isErrorMessage,
          stepTitle: title,
          stepContent: content
        });
      }
    }
  }),
  // Next button
  !hideNextButton && !isLastStep && !isErrorMessage && /*#__PURE__*/React.createElement(StyledButton, {
    text: t('tour_step_action_next'),
    onClick: function onClick(e) {
      incrementStep();
      onClickNext(e);
      if (onTourTooltipClickNext) {
        onTourTooltipClickNext({
          tourStepId: currentActiveTourStep.id,
          stepId: step.id,
          stepTitle: title,
          stepContent: content
        });
      }
    }
  }),
  // Finish button
  isLastStep && !isErrorMessage && /*#__PURE__*/React.createElement(StyledButton, {
    text: t('tour_step_action_finish'),
    onClick: function onClick() {
      completeTour();
      if (onTourTooltipClickDone) {
        onTourTooltipClickDone({
          tourStepId: currentActiveTourStep.id,
          stepId: step.id,
          stepTitle: title,
          stepContent: content
        });
      }
    }
  })));
}
export function TourTooltip(props) {
  var step = props.step;
  return step.isModal ? /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(Modal, {
    open: true
  }, /*#__PURE__*/React.createElement(TourTooltipContent, props)), document.body) : /*#__PURE__*/React.createElement(TourTooltipContent, props);
}